import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { DataServiceFactory } from '@/factories'
import type {
	ICountry,
	ICurrency,
	IDepartment,
	IDropdown,
	IEmployee,
	ILocation,
	IPaymentMethod,
	IPaymentTerm,
	ISalesperson,
	IState
} from '@/interfaces'
import { CommonService, EntraIDAuthConfig } from '@/services'
import { useAuthStore } from '@/stores'

export const useCommonStore = defineStore('common-store', () => {
	const initialized = ref(false)
	const authService = ref(DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig))
	const commonService = ref(DataServiceFactory.createService('Common', authService.value) as CommonService)
	const salespersons = ref<ISalesperson[]>([])
	const countries = ref<ICountry[]>([])
	const states = ref<IState[]>([])
	const currencies = ref<ICurrency[]>([])
	const paymentMethods = ref<IPaymentMethod[]>([])
	const paymentTerms = ref<IPaymentTerm[]>([])
	const locations = ref<ILocation[]>([])
	const departments = ref<IDepartment[]>([])
	const employees = ref<IEmployee[]>([])

	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	const salesPersonDropdown = computed<IDropdown[]>(() => {
		if (
			getAuthStore.value.roles.includes('App.Admin') ||
			getAuthStore.value.roles.includes('ClientIntake.Approver') ||
			getAuthStore.value.isSuperUser ||
			getAuthStore.value.salesGroup.length === 0
		) {
			return salespersons.value
				.map((person) => ({
					name: person.name,
					code: person.id
				}))
				.sort((a, b) => a.name.localeCompare(b.name))
		} else {
			return salespersons.value
				.filter((person) => getAuthStore.value.salesGroup.includes(person.id))
				.map((person) => ({
					name: person.name,
					code: person.id
				}))
				.sort((a, b) => a.name.localeCompare(b.name))
		}
	})

	const activeSalesPersonDropdown = computed<IDropdown[]>(() => {
		if (
			getAuthStore.value.roles.includes('App.Admin') ||
			getAuthStore.value.roles.includes('ClientIntake.Approver') ||
			getAuthStore.value.isSuperUser ||
			getAuthStore.value.salesGroup.length === 0
		) {
			return activeSalespersons.value
				.map((person) => ({
					name: person.name,
					code: person.id
				}))
				.sort((a, b) => a.name.localeCompare(b.name))
		} else {
			return activeSalespersons.value
				.filter((person) => getAuthStore.value.salesGroup.includes(person.id))
				.map((person) => ({
					name: person.name,
					code: person.id
				}))
				.sort((a, b) => a.name.localeCompare(b.name))
		}
	})

	const activeSalespersons = computed<ISalesperson[]>(() => {
		return salespersons.value.filter((person) => person.employeeActive === true)
	})

	const mySalesPersons = computed<ISalesperson[]>(() => {
		if (
			getAuthStore.value.roles.includes('App.Admin') ||
			getAuthStore.value.roles.includes('ClientIntake.Approver') ||
			getAuthStore.value.isSuperUser ||
			getAuthStore.value.salesGroup.length === 0
		) {
			return salespersons.value
		} else {
			return salespersons.value.filter((person) => getAuthStore.value.salesGroup.includes(person.id))
		}
	})

	const countriesDropdown = computed<IDropdown[]>(() => {
		return countries.value.map((country) => ({
			name: country.name,
			code: country.code
		}))
	})

	const paymentTermsDropdown = computed<IDropdown[]>(() => {
		return paymentTerms.value.map((term) => ({
			name: term.paymentGroup,
			code: term.groupNum
		}))
	})

	const currenciesDropdown = computed<IDropdown[]>(() => {
		return currencies.value.map((currency) => ({
			name: currency.name,
			code: currency.code
		}))
	})

	const employeeDropdown = computed<IDropdown[]>(() => {
		return employees.value
			.map((employee) => ({
				name: employee.displayName,
				code: employee.id
			}))
			.sort((a, b) => a.name.localeCompare(b.name))
	})

	const locationsDropdown = computed<IDropdown[]>(() => {
		return locations.value.map((location) => ({
			name: location.code,
			code: location.shortName
		}))
	})

	const salespersonSiteAliasMap = computed<Record<number, string>>(() => {
		const branchToAliasMap: Record<string, string> = {
			hillsboro: 'PDX',
			'morgan hill': 'MH',
			irvine: 'LA',
			chicago: 'CHI',
			'north carolina': 'NC',
			hartford: 'HAR',
			trumbull: 'TRU'
		}

		const result: Record<number, string> = {}

		salespersons.value.forEach((person) => {
			if (person.branch) {
				const branchLower = person.branch.toLowerCase()

				for (const [branchKey, alias] of Object.entries(branchToAliasMap)) {
					if (branchLower.includes(branchKey)) {
						result[person.id] = alias
						break
					}
				}
			}
		})

		return result
	})

	async function getSalespersons() {
		if (commonService.value) {
			await commonService.value
				.getSalespersons()
				.then((res) => (salespersons.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}

	async function getCountries() {
		if (commonService.value) {
			await commonService.value
				.getCountries()
				.then((res) => (countries.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}

	async function getAllStates() {
		if (commonService.value) {
			await commonService.value
				.getAllStates()
				.then((res) => (states.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}

	function getFilteredStates(country: string) {
		const code = country
		if (code !== undefined && code !== null && code != '') {
			return states.value.filter((state) => state.country === code)
		} else {
			return []
		}
	}

	async function getCurrencies() {
		if (commonService.value) {
			await commonService.value
				.getCurrencies()
				.then((res) => (currencies.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}
	async function getPaymentMethods() {
		if (commonService.value) {
			await commonService.value
				.getPaymentMethods()
				.then((res) => (paymentMethods.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}
	async function getPaymentTerms() {
		if (commonService.value) {
			await commonService.value
				.getPaymentTerms()
				.then((res) => (paymentTerms.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}
	async function getLocations() {
		if (commonService.value) {
			await commonService.value
				.getLocations()
				.then((res) => (locations.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}
	async function getDepartments() {
		if (commonService.value) {
			await commonService.value
				.getDepartments()
				.then((res) => (departments.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}
	async function getEmployees() {
		if (commonService.value) {
			await commonService.value
				.getEmployees()
				.then((res) => (employees.value = res))
				.catch((e: Error) => {
					console.log(e)
				})
		}
	}

	function getEmployee(id: number): IEmployee {
		return employees.value.find((employee) => employee.id === id) || ({} as IEmployee)
	}

	function getSalesperson(id: number): ISalesperson {
		return salespersons.value.find((person) => person.id === id) || ({} as ISalesperson)
	}

	if (!salespersons.value.length) {
		getSalespersons()
	}
	// if (!activeSalespersons.value.length) {
	// 	getActiveSalespersons()
	// }
	if (!countries.value.length) {
		getCountries()
	}
	if (!states.value.length) {
		getAllStates()
	}
	if (!currencies.value.length) {
		getCurrencies()
	}
	if (!paymentMethods.value.length) {
		getPaymentMethods()
	}
	if (!paymentTerms.value.length) {
		getPaymentTerms()
	}
	if (!locations.value.length) {
		getLocations()
	}
	if (!departments.value.length) {
		getDepartments()
	}
	if (!employees.value.length) {
		getEmployees()
	}

	initialized.value = true

	return {
		initialized,
		commonService,
		salespersons,
		activeSalespersons,
		countries,
		states,
		currencies,
		paymentMethods,
		paymentTerms,
		locations,
		departments,
		employees,
		salesPersonDropdown,
		activeSalesPersonDropdown,
		countriesDropdown,
		paymentTermsDropdown,
		currenciesDropdown,
		employeeDropdown,
		locationsDropdown,
		salespersonSiteAliasMap,
		mySalesPersons,
		getSalespersons,
		getCountries,
		getAllStates,
		getFilteredStates,
		getCurrencies,
		getPaymentMethods,
		getPaymentTerms,
		getLocations,
		getDepartments,
		getEmployees,
		getEmployee,
		getSalesperson
	}
})
